import { useQuery } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface StoredVendorDetails {
  id: string;
  name: string;
  address: string;
  distance: string;
  distance_meters?: number | null;
  distance_formatted?: string;
  distance_miles?: number;
  services: string;
  website?: string | null;
  phone: string;
  phone_type: string;
  email?: string | null;
  reason_for_recommendation: string;
  match_score: number;
  emergency_service: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetVendorsBySearchIdResponse {
  success: boolean;
  data: StoredVendorDetails[];
}

const METHOD = 'GET';
const ENDPOINT = 'vendor/search/:searchId/vendors';
export const QUERY_KEY = (searchId: string) => getQueryKey(METHOD, ENDPOINT, { searchId });

export const useGetVendorsBySearchId = (searchId: string, enabled = true) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!searchId,
    queryKey: QUERY_KEY(searchId),
    queryFn: async () => {
      const response = await handleApiCall<GetVendorsBySearchIdResponse>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, { searchId })
      );
      return response.data || [];
    },
    refetchInterval: (query) => {
      // Refetch every 5 seconds if we don't have distance data for all vendors
      const vendors = query.state.data || [];
      const hasAllDistances = vendors.every(v => v.distance_meters !== null);
      return hasAllDistances ? false : 5000;
    },
    staleTime: 30000, // Consider data stale after 30 seconds
  });
};